import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { TID } from "@/lib/testIds";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle } from "@phosphor-icons/react";

const STEPS = ["Describe", "Company", "Contact", "Package", "Review"];

const emptyCriteria = {
  countries: [], regions: [], excluded_countries: [], industries_include: [], industries_exclude: [],
  employee_min: null, employee_max: null, revenue_min: "", revenue_max: "", company_type: "",
  company_ownership: "", technologies_include: [], technologies_exclude: [], target_accounts: [],
  domains: [], extra_company: "",
  departments: [], seniorities: [], exact_titles: [], related_titles_allowed: true, excluded_titles: [],
  max_contacts_per_company: null, contact_locations: [], email_required: true, phone_required: false,
  linkedin_required: false, extra_contact: "",
  package_preference: "not_sure", email_verification: false, phone_enrichment: false, human_review: false,
  additional_output_fields: [], output_format: "csv",
};

const CSV = (v) => (Array.isArray(v) ? v.join(", ") : "");
const parseCSV = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function NewRequest() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [form, setForm] = useState({
    title: "",
    plain_english: (typeof sessionStorage !== "undefined" && sessionStorage.getItem("er_query")) || "",
    intended_use: "outbound_sales",
    requested_quantity: 1000,
    preferred_delivery_date: "",
    criteria: { ...emptyCriteria },
  });
  const [confirmChecks, setConfirmChecks] = useState({ correct: false, terms: false, lawful: false });

  useEffect(() => {
    if (form.plain_english && !form.title) {
      const words = form.plain_english.split(/\s+/).slice(0, 8).join(" ");
      setForm((f) => ({ ...f, title: words + (form.plain_english.split(/\s+/).length > 8 ? "…" : "") }));
    }
    // eslint-disable-next-line
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updC = (k, v) => setForm((f) => ({ ...f, criteria: { ...f.criteria, [k]: v } }));

  const saveDraft = async () => {
    setSaving(true);
    try {
      if (!requestId) {
        const { data } = await api.post("/requests", form);
        setRequestId(data.request_id);
        toast.success("Draft saved");
      } else {
        await api.patch(`/requests/${requestId}`, form);
        toast.success("Draft updated");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const submit = async () => {
    if (!(confirmChecks.correct && confirmChecks.terms && confirmChecks.lawful)) {
      toast.error("Please confirm all boxes to submit"); return;
    }
    try {
      let id = requestId;
      if (!id) {
        const { data } = await api.post("/requests", form);
        id = data.request_id;
      } else {
        await api.patch(`/requests/${id}`, form);
      }
      await api.post(`/requests/${id}/submit`);
      toast.success("Request submitted!");
      try { sessionStorage.removeItem("er_query"); } catch (err) { console.warn("[NewRequest] sessionStorage cleanup blocked:", err); }
      navigate(`/requests/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    }
  };

  return (
    <div className="App min-h-screen">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-10">
        <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-2">New Audience Request</div>
        <h1 className="font-heading font-black tracking-tighter text-4xl md:text-5xl mb-8">Describe your audience.</h1>

        {/* Stepper */}
        <div className="flex items-center gap-1 mb-10 overflow-x-auto">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2 shrink-0">
              <div className={`h-8 w-8 grid place-items-center border ${i <= step ? "bg-foreground text-background border-foreground" : "bg-white text-muted-foreground border-border"} font-mono text-xs`}>
                {i < step ? "✓" : i + 1}
              </div>
              <div className={`text-xs font-medium ${i === step ? "text-foreground" : "text-muted-foreground"} pr-3`}>{s}</div>
              {i < STEPS.length - 1 && <div className="w-6 md:w-10 h-px bg-border" />}
            </div>
          ))}
        </div>

        <div className="border border-border bg-white p-6 md:p-8">
          {/* Step 1 */}
          {step === 0 && (
            <div className="space-y-5">
              <div>
                <Label htmlFor="title">Request title</Label>
                <Input id="title" data-testid={TID.request.title} value={form.title} onChange={(e) => upd("title", e.target.value)} className="mt-1" placeholder="e.g. IT Directors at US manufacturers" />
              </div>
              <div>
                <Label htmlFor="pe">Describe the audience you need</Label>
                <Textarea id="pe" data-testid={TID.request.plainEnglish} rows={6} value={form.plain_english} onChange={(e) => upd("plain_english", e.target.value)} className="mt-1 font-mono text-sm" placeholder="Plain English works best." />
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Intended use</Label>
                  <Select value={form.intended_use} onValueChange={(v) => upd("intended_use", v)}>
                    <SelectTrigger data-testid={TID.request.intendedUse} className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="outbound_sales">Outbound sales</SelectItem>
                      <SelectItem value="market_expansion">Market expansion</SelectItem>
                      <SelectItem value="event_promotion">Event promotion</SelectItem>
                      <SelectItem value="recruitment">Recruitment</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Required quantity</Label>
                  <Input type="number" data-testid={TID.request.quantity} value={form.requested_quantity || ""} onChange={(e) => upd("requested_quantity", parseInt(e.target.value) || null)} className="mt-1" />
                </div>
                <div>
                  <Label>Preferred delivery</Label>
                  <Input type="date" value={form.preferred_delivery_date || ""} onChange={(e) => upd("preferred_delivery_date", e.target.value)} className="mt-1" />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Company */}
          {step === 1 && (
            <div className="space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Target countries (comma-separated)</Label>
                  <Input data-testid={TID.request.countries} defaultValue={CSV(form.criteria.countries)} onBlur={(e) => updC("countries", parseCSV(e.target.value))} className="mt-1" placeholder="United States, Canada" />
                </div>
                <div>
                  <Label>Excluded countries</Label>
                  <Input defaultValue={CSV(form.criteria.excluded_countries)} onBlur={(e) => updC("excluded_countries", parseCSV(e.target.value))} className="mt-1" />
                </div>
                <div>
                  <Label>Industries to include</Label>
                  <Input data-testid={TID.request.industries} defaultValue={CSV(form.criteria.industries_include)} onBlur={(e) => updC("industries_include", parseCSV(e.target.value))} className="mt-1" placeholder="Software, Manufacturing" />
                </div>
                <div>
                  <Label>Industries to exclude</Label>
                  <Input defaultValue={CSV(form.criteria.industries_exclude)} onBlur={(e) => updC("industries_exclude", parseCSV(e.target.value))} className="mt-1" />
                </div>
                <div>
                  <Label>Employees minimum</Label>
                  <Input type="number" data-testid={TID.request.empMin} defaultValue={form.criteria.employee_min || ""} onBlur={(e) => updC("employee_min", parseInt(e.target.value) || null)} className="mt-1" />
                </div>
                <div>
                  <Label>Employees maximum</Label>
                  <Input type="number" data-testid={TID.request.empMax} defaultValue={form.criteria.employee_max || ""} onBlur={(e) => updC("employee_max", parseInt(e.target.value) || null)} className="mt-1" />
                </div>
                <div>
                  <Label>Technologies used</Label>
                  <Input defaultValue={CSV(form.criteria.technologies_include)} onBlur={(e) => updC("technologies_include", parseCSV(e.target.value))} className="mt-1" placeholder="SAP, Salesforce" />
                </div>
                <div>
                  <Label>Technologies excluded</Label>
                  <Input defaultValue={CSV(form.criteria.technologies_exclude)} onBlur={(e) => updC("technologies_exclude", parseCSV(e.target.value))} className="mt-1" />
                </div>
              </div>
              <div>
                <Label>Target account list (company names, comma-separated)</Label>
                <Textarea rows={3} defaultValue={CSV(form.criteria.target_accounts)} onBlur={(e) => updC("target_accounts", parseCSV(e.target.value))} className="mt-1" placeholder="Acme Corp, Widget Inc." />
              </div>
            </div>
          )}

          {/* Step 3: Contact */}
          {step === 2 && (
            <div className="space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Departments / job functions</Label>
                  <Input data-testid={TID.request.departments} defaultValue={CSV(form.criteria.departments)} onBlur={(e) => updC("departments", parseCSV(e.target.value))} className="mt-1" placeholder="IT, Engineering" />
                </div>
                <div>
                  <Label>Seniority levels</Label>
                  <Input data-testid={TID.request.seniorities} defaultValue={CSV(form.criteria.seniorities)} onBlur={(e) => updC("seniorities", parseCSV(e.target.value))} className="mt-1" placeholder="Director, VP, C-Level" />
                </div>
                <div>
                  <Label>Exact job titles</Label>
                  <Input data-testid={TID.request.titles} defaultValue={CSV(form.criteria.exact_titles)} onBlur={(e) => updC("exact_titles", parseCSV(e.target.value))} className="mt-1" placeholder="IT Director, CTO" />
                </div>
                <div>
                  <Label>Excluded titles</Label>
                  <Input defaultValue={CSV(form.criteria.excluded_titles)} onBlur={(e) => updC("excluded_titles", parseCSV(e.target.value))} className="mt-1" />
                </div>
                <div>
                  <Label>Max contacts per company</Label>
                  <Input type="number" defaultValue={form.criteria.max_contacts_per_company || ""} onBlur={(e) => updC("max_contacts_per_company", parseInt(e.target.value) || null)} className="mt-1" />
                </div>
                <div>
                  <Label>Contact locations</Label>
                  <Input defaultValue={CSV(form.criteria.contact_locations)} onBlur={(e) => updC("contact_locations", parseCSV(e.target.value))} className="mt-1" placeholder="United States" />
                </div>
              </div>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={form.criteria.email_required} onCheckedChange={(v) => updC("email_required", !!v)} /> Business email required
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={form.criteria.phone_required} onCheckedChange={(v) => updC("phone_required", !!v)} /> Phone required
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={form.criteria.linkedin_required} onCheckedChange={(v) => updC("linkedin_required", !!v)} /> LinkedIn URL required
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={form.criteria.related_titles_allowed} onCheckedChange={(v) => updC("related_titles_allowed", !!v)} /> Related titles allowed
                </label>
              </div>
            </div>
          )}

          {/* Step 4: Package */}
          {step === 3 && (
            <div className="space-y-5">
              <div>
                <Label className="mb-2 block">Data package preference</Label>
                <div className="grid md:grid-cols-3 gap-3">
                  {[
                    { k: "standard", t: "Standard", d: "Fastest turnaround. Emails included where available." },
                    { k: "verified", t: "Verified", d: "Emails re-verified close to fulfilment. Invalid replaced." },
                    { k: "not_sure", t: "Not sure", d: "Let the analyst recommend the best fit." },
                  ].map((o) => (
                    <button
                      key={o.k}
                      type="button"
                      data-testid={`${TID.request.package}-${o.k}`}
                      onClick={() => updC("package_preference", o.k)}
                      className={`text-left p-4 border-2 rounded-sm ${form.criteria.package_preference === o.k ? "border-primary bg-primary/5" : "border-border hover:border-foreground"}`}
                    >
                      <div className="font-semibold mb-1">{o.t}</div>
                      <div className="text-xs text-muted-foreground">{o.d}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.criteria.email_verification} onCheckedChange={(v) => updC("email_verification", !!v)} /> Email verification required</label>
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.criteria.phone_enrichment} onCheckedChange={(v) => updC("phone_enrichment", !!v)} /> Phone enrichment</label>
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.criteria.human_review} onCheckedChange={(v) => updC("human_review", !!v)} /> Human review requested</label>
              </div>
              <div>
                <Label>Additional output fields (comma-separated)</Label>
                <Input defaultValue={CSV(form.criteria.additional_output_fields)} onBlur={(e) => updC("additional_output_fields", parseCSV(e.target.value))} className="mt-1" placeholder="LinkedIn URL, HQ Country" />
              </div>
              <div>
                <Label>Output format</Label>
                <Select value={form.criteria.output_format} onValueChange={(v) => updC("output_format", v)}>
                  <SelectTrigger data-testid={TID.request.format} className="mt-1 w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="csv">CSV</SelectItem>
                    <SelectItem value="xlsx">XLSX</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 5: Review */}
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-2">Summary</div>
                <div className="text-xl font-heading font-bold">{form.title || "Untitled"}</div>
                <p className="text-sm text-muted-foreground font-mono mt-2 whitespace-pre-wrap">{form.plain_english}</p>
              </div>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Intended use:</span> <b>{form.intended_use.replace(/_/g, " ")}</b></div>
                <div><span className="text-muted-foreground">Requested quantity:</span> <b>{form.requested_quantity}</b></div>
                <div><span className="text-muted-foreground">Countries:</span> {form.criteria.countries.join(", ") || "—"}</div>
                <div><span className="text-muted-foreground">Industries:</span> {form.criteria.industries_include.join(", ") || "—"}</div>
                <div><span className="text-muted-foreground">Employees:</span> {form.criteria.employee_min || "?"} – {form.criteria.employee_max || "?"}</div>
                <div><span className="text-muted-foreground">Seniorities:</span> {form.criteria.seniorities.join(", ") || "—"}</div>
                <div><span className="text-muted-foreground">Titles:</span> {form.criteria.exact_titles.join(", ") || "—"}</div>
                <div><span className="text-muted-foreground">Package:</span> {form.criteria.package_preference}</div>
              </div>
              <div className="border-t border-border pt-4 space-y-3">
                <label className="flex items-start gap-3 text-sm"><Checkbox data-testid={TID.request.confirm} checked={confirmChecks.correct} onCheckedChange={(v) => setConfirmChecks({ ...confirmChecks, correct: !!v })} className="mt-0.5" /> I confirm the criteria above are correct.</label>
                <label className="flex items-start gap-3 text-sm"><Checkbox data-testid={TID.request.terms} checked={confirmChecks.terms} onCheckedChange={(v) => setConfirmChecks({ ...confirmChecks, terms: !!v })} className="mt-0.5" /> I agree to the Terms of Service and Data Usage Policy.</label>
                <label className="flex items-start gap-3 text-sm"><Checkbox checked={confirmChecks.lawful} onCheckedChange={(v) => setConfirmChecks({ ...confirmChecks, lawful: !!v })} className="mt-0.5" /> I confirm this data will be used for a lawful business purpose.</label>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-6 gap-3">
          <Button variant="outline" data-testid={TID.request.stepPrev} disabled={step === 0} onClick={() => setStep(step - 1)} className="rounded-sm">
            <ArrowLeft size={14} className="mr-2" /> Back
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" data-testid={TID.request.saveDraft} onClick={saveDraft} disabled={saving} className="rounded-sm">
              {saving ? "Saving…" : "Save draft"}
            </Button>
            {step < STEPS.length - 1 ? (
              <Button data-testid={TID.request.stepNext} onClick={() => setStep(step + 1)} className="rounded-sm">
                Next <ArrowRight size={14} className="ml-2" />
              </Button>
            ) : (
              <Button data-testid={TID.request.submit} onClick={submit} className="rounded-sm">
                <CheckCircle size={14} className="mr-2" /> Submit request
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
